/**
 * What to tell someone whose phone number was refused.
 *
 * One message used to cover every refusal: "International numbers must start
 * with + and your country code". That fits a number too short or too long to be
 * real. It does not fit +1 520 104 1024, which is the right length and already
 * starts with +1 -- it fails because no US exchange starts with 0 or 1. On
 * 2026-09-22 a visitor with that shape was told to add the + they had typed.
 *
 * So the refusal is split by shape. A number the right length for its country
 * is a typo, and the message says to check the digits. Under +1 it can also be
 * a foreign number the login form put +1 in front of, so that message keeps the
 * country-code hint as its second sentence. Any other shape is a format
 * problem and keeps the original message.
 *
 * No imports: the login form uses these, and libphonenumber-js is not worth
 * shipping to the browser for a regex's worth of NANP rules.
 */

/** The number's length is wrong for any country we could read it as. */
export const PHONE_FORMAT_MESSAGE =
  'The phone number entered is not valid. International numbers must start with + and your country code (e.g. +44).';

/** A number the right length for its country, but not one that exists. */
export const PHONE_NOT_REAL_MESSAGE =
  "That phone number doesn't exist. Check the digits and try again.";

/** The same, under +1, where it may also be a foreign number missing its code. */
export const NANP_PHONE_NOT_REAL_MESSAGE =
  "That phone number doesn't exist. Check the digits. Numbers outside the US and Canada must start with + and the country code (e.g. +44).";

/** +1 then 10 digits: the shape of a US or Canada number, real or not. */
export const NANP_SHAPE = /^\+1\d{10}$/;

/** NPA-NXX-XXXX: neither the area code nor the exchange can start with 0 or 1. */
export const NANP_DIALABLE = /^\+1[2-9]\d{2}[2-9]\d{6}$/;

/**
 * The login form's check before it asks for a code: null when a +1 number
 * could be dialed, otherwise the message to show. Other country codes pass
 * through to the server, which has the metadata to judge them.
 */
export function nanpPhoneProblem(phone: string): string | null {
  if (!phone.startsWith('+1') || NANP_DIALABLE.test(phone)) return null;
  return NANP_SHAPE.test(phone) ? NANP_PHONE_NOT_REAL_MESSAGE : PHONE_FORMAT_MESSAGE;
}
